import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { PageHeader } from "@/components/shared/page-header";

interface StateRow {
  state: string;
  priorYrCustomers: number;
  ffThisYr: number;
  currentK: number | null;
  oppsPerMonthEst: number;
}
interface Territory {
  key: string;
  label: string;
  consultant: string;
  division: string;
  divisionHead: string;
  states: string[];
  baselineK: number;
  currentK: number | null;
  deltaK: number | null;
  priorYrCustomers: number;
  ffThisYr: number;
  oppsPerMonth: number;
  oppsPerMonthEst: number;
  capStatus: "ok" | "over_target" | "over_stretch";
  stateBreakdown: StateRow[];
}
interface DivisionRow {
  division: string;
  head: string;
  currentK: number | null;
  priorYrCustomers: number;
  oppsPerMonthEst: number;
}
interface ProductOption {
  key: string;
  label: string;
}
interface Data {
  territories: Territory[];
  divisions: DivisionRow[];
  meta: {
    coverage: number;
    oppCap: number;
    oppStretch: number;
    asOf: string;
    product: string;
    productOptions: ProductOption[];
  };
}

const fmtK = (k: number | null) => (k === null ? "—" : k.toFixed(2));
const capColor = (s: string) =>
  s === "over_stretch"
    ? "text-amber-600"
    : s === "over_target"
      ? "text-amber-500"
      : "text-emerald-600";
const capLabel = (s: string) =>
  s === "over_stretch" ? "over 100 — hire" : s === "over_target" ? "over 80" : "ok";

export function TerritoriesPage() {
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [product, setProduct] = useState("engagement");
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  useEffect(() => {
    setLoading(true);
    fetch(`/api/territories?product=${product}`)
      .then(async (r) => {
        if (!r.ok)
          throw new Error(
            r.status === 403 ? "You don't have access to this page." : "Failed to load",
          );
        return r.json();
      })
      .then((d) => {
        setData(d);
        setError("");
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [product]);

  if (loading && !data)
    return <div className="p-8 text-muted-foreground">Loading territories…</div>;
  if (error) return <div className="p-8 text-destructive">{error}</div>;
  if (!data) return null;

  return (
    <div className="space-y-8">
      <PageHeader
        title="Territories"
        description={`Referral rate (k) and opportunity load by consultant territory. Customer location known for ~${data.meta.coverage}% of orders.`}
      />

      {/* Product filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Product:</span>
        <div className="inline-flex items-center gap-1 rounded-lg border bg-card p-1">
          {data.meta.productOptions.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setProduct(p.key)}
              className={
                "rounded-md px-3 py-1.5 text-sm transition-colors " +
                (product === p.key
                  ? "bg-primary font-medium text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted")
              }
            >
              {p.label}
            </button>
          ))}
        </div>
        {loading && <span className="text-xs text-muted-foreground">updating…</span>}
      </div>

      {/* Division summary */}
      <div className="grid gap-4 md:grid-cols-2">
        {data.divisions.map((d) => (
          <div key={d.division} className="rounded-xl border bg-card p-5">
            <div className="text-sm text-muted-foreground">
              {d.division} — {d.head}
            </div>
            <div className="mt-2 flex items-end gap-6">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Division k
                </div>
                <div className="font-serif text-3xl">{fmtK(d.currentK)}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Customers/yr
                </div>
                <div className="font-serif text-3xl tabular-nums">
                  {d.priorYrCustomers.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Opps/mo (est)
                </div>
                <div className="font-serif text-3xl tabular-nums">{d.oppsPerMonthEst}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Territory table by division */}
      {["East", "West"].map((div) => (
        <div key={div}>
          <h2 className="mb-3 font-serif text-lg">
            {div} — {data.territories.find((t) => t.division === div)?.divisionHead}
          </h2>
          <div className="overflow-hidden rounded-xl border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Consultant</th>
                  <th className="px-4 py-3 font-semibold">Territory</th>
                  <th className="px-4 py-3 font-semibold text-right">Baseline k</th>
                  <th className="px-4 py-3 font-semibold text-right">Current k</th>
                  <th className="px-4 py-3 font-semibold text-right">Δ</th>
                  <th className="px-4 py-3 font-semibold text-right">Opps/mo</th>
                  <th className="px-4 py-3 font-semibold">Capacity</th>
                </tr>
              </thead>
              <tbody>
                {data.territories
                  .filter((t) => t.division === div)
                  .map((t) => {
                    const isOpen = open.has(t.key);
                    return (
                      <Fragment key={t.key}>
                        <tr
                          className="border-b last:border-0 cursor-pointer hover:bg-muted/40"
                          onClick={() => toggle(t.key)}
                        >
                          <td className="px-4 py-3 font-medium">
                            <span className="inline-flex items-center gap-1.5">
                              {isOpen ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )}
                              {t.consultant}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div>{t.label}</div>
                            <div className="text-[11px] text-muted-foreground">
                              {t.states.length} state{t.states.length === 1 ? "" : "s"} ·{" "}
                              {t.priorYrCustomers} customers/yr
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                            {fmtK(t.baselineK)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums font-medium">
                            {fmtK(t.currentK)}
                          </td>
                          <td
                            className={
                              "px-4 py-3 text-right tabular-nums font-medium " +
                              (t.deltaK === null
                                ? ""
                                : t.deltaK >= 0
                                  ? "text-emerald-600"
                                  : "text-destructive")
                            }
                          >
                            {t.deltaK === null
                              ? "—"
                              : (t.deltaK >= 0 ? "+" : "") + t.deltaK.toFixed(2)}
                          </td>
                          <td
                            className={
                              "px-4 py-3 text-right tabular-nums font-medium " +
                              capColor(t.capStatus)
                            }
                          >
                            {t.oppsPerMonthEst}
                          </td>
                          <td className={"px-4 py-3 text-xs " + capColor(t.capStatus)}>
                            {capLabel(t.capStatus)}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="border-b bg-muted/20">
                            <td colSpan={7} className="p-0">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                                    <th className="py-2 pl-11 pr-4 font-semibold">State</th>
                                    <th className="px-4 py-2 font-semibold text-right">
                                      Customers/yr
                                    </th>
                                    <th className="px-4 py-2 font-semibold text-right">
                                      Referrals (12mo)
                                    </th>
                                    <th className="px-4 py-2 font-semibold text-right">k</th>
                                    <th className="px-4 py-2 font-semibold text-right">Opps/mo</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {t.stateBreakdown.map((s) => (
                                    <tr key={s.state} className="text-muted-foreground">
                                      <td className="py-1.5 pl-11 pr-4">{s.state}</td>
                                      <td className="px-4 py-1.5 text-right tabular-nums">
                                        {s.priorYrCustomers}
                                      </td>
                                      <td className="px-4 py-1.5 text-right tabular-nums">
                                        {s.ffThisYr}
                                      </td>
                                      <td className="px-4 py-1.5 text-right tabular-nums">
                                        {s.currentK === null ? "—" : s.currentK.toFixed(2)}
                                      </td>
                                      <td className="px-4 py-1.5 text-right tabular-nums">
                                        {s.oppsPerMonthEst}
                                      </td>
                                    </tr>
                                  ))}
                                  <tr className="border-t font-medium">
                                    <td className="py-1.5 pl-11 pr-4">Territory total</td>
                                    <td className="px-4 py-1.5 text-right tabular-nums">
                                      {t.priorYrCustomers}
                                    </td>
                                    <td className="px-4 py-1.5 text-right tabular-nums">
                                      {t.ffThisYr}
                                    </td>
                                    <td className="px-4 py-1.5 text-right tabular-nums">
                                      {fmtK(t.currentK)}
                                    </td>
                                    <td className="px-4 py-1.5 text-right tabular-nums">
                                      {t.oppsPerMonthEst}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <p className="text-xs text-muted-foreground">
        k = referral (friends/family + affiliate) closed customers (trailing 12mo) ÷ total closed
        customers (prior 12mo). Baseline set at launch; Δ shows movement since. Opportunity target{" "}
        {data.meta.oppCap}/mo, {data.meta.oppStretch}/mo = actively hiring. As of {data.meta.asOf}.
      </p>
    </div>
  );
}
